/**
 * FASE 7.2 — Data Quality Center page.
 * READ-ONLY. Consulta /api/data-quality/overview e mostra score + 9 aree + actions.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  CheckCircle2, AlertTriangle, XCircle, MinusCircle, ChevronDown,
  ChevronUp, RefreshCw, ArrowRight, Info, ShieldCheck,
} from "lucide-react";

const STATUS_META = {
  COMPLETE:     { icon: CheckCircle2, cls: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200", label: "Completo" },
  PARTIAL:      { icon: AlertTriangle, cls: "text-amber-700",  bg: "bg-amber-50 border-amber-200",   label: "Parziale" },
  INSUFFICIENT: { icon: XCircle,       cls: "text-rose-700",   bg: "bg-rose-50 border-rose-200",     label: "Insufficiente" },
  "N/A":        { icon: MinusCircle,   cls: "text-stone-500",  bg: "bg-stone-50 border-stone-200",   label: "Non applicabile" },
};

const AREA_TITLE = {
  vendite: "Vendite",
  prodotti: "Performance Prodotti",
  food_cost: "Food Cost / Menu / Ricette",
  magazzino: "Magazzino",
  personale: "Personale",
  reorder: "Smart Reorder",
  forecast: "Forecast",
  haccp: "HACCP",
  setup: "Setup economico",
};

const SEV_META = {
  high:   { color: "text-rose-700",   emoji: "🔴", label: "Alta" },
  medium: { color: "text-amber-700",  emoji: "🟠", label: "Media" },
  low:    { color: "text-yellow-700", emoji: "🟡", label: "Bassa" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META["N/A"];
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs border ${m.bg} ${m.cls}`}>
      <Icon size={12} strokeWidth={2} /> {m.label}
    </span>
  );
}

function AreaCard({ area, expanded, onToggle }) {
  const meta = STATUS_META[area.status] || STATUS_META["N/A"];
  return (
    <div className={`border rounded-lg overflow-hidden bg-white ${meta.bg}`}
         data-testid={`dq-area-${area.area}`}>
      <button onClick={onToggle}
              className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/60"
              data-testid={`dq-area-${area.area}-toggle`}>
        <div className="text-left flex items-center gap-3">
          <StatusBadge status={area.status} />
          <div>
            <div className="font-medium">{AREA_TITLE[area.area] || area.area}</div>
            <div className="text-xs text-neutral-500">
              {area.score != null ? `Score ${area.score} · ` : ""}
              {area.confidence_label && area.status !== "N/A" ? `Confidence ${area.confidence_label}` : "—"}
              {area.kind === "current_state" && <span className="ml-1 text-neutral-400">· stato attuale</span>}
              {area.kind === "period" && <span className="ml-1 text-neutral-400">· periodo</span>}
            </div>
          </div>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {expanded && (
        <div className="border-t px-4 py-3 space-y-3 bg-white text-sm">
          <div className="text-neutral-700">{area.reason}</div>
          {area.metrics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              {Object.entries(area.metrics).slice(0, 8).map(([k, v]) => (
                <div key={k} className="border border-stone-200 rounded p-2 bg-stone-50">
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">{k}</div>
                  <div className="text-sm font-medium tabular-nums">
                    {typeof v === "object" ? "—" : String(v)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {(area.issues || []).length > 0 && (
            <div className="rounded border border-amber-300 bg-amber-50 p-2 text-xs">
              <div className="font-medium text-amber-900 mb-1">Cosa manca</div>
              <ul className="list-disc list-inside space-y-0.5 text-amber-900">
                {area.issues.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </div>
          )}
          {(area.recommended_actions || []).length > 0 && (
            <div className="space-y-2">
              {area.recommended_actions.map((a, i) => (
                <div key={i} className="border border-stone-200 rounded p-2">
                  <div className="flex justify-between items-start gap-2">
                    <div>
                      <div className={`text-sm font-medium ${SEV_META[a.severity]?.color || ""}`}>
                        {SEV_META[a.severity]?.emoji} {a.title}
                      </div>
                      {a.detail && <div className="text-xs text-neutral-600 mt-1">{a.detail}</div>}
                      {a.impact && <div className="text-xs text-neutral-500 mt-1 italic">Impatto: {a.impact}</div>}
                    </div>
                    {a.cta_href && (
                      <Link to={a.cta_href} data-testid={`dq-cta-${a.code}`}>
                        <Button size="sm" variant="outline">
                          {a.cta_label || "Vai"} <ArrowRight size={12} className="ml-1" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function DataQuality() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/data-quality/overview?days=${days}`);
      setData(data);
    } catch (e) {
      toast.error("Errore caricamento Data Quality");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [days]);

  const overallLabel = (() => {
    if (!data) return "";
    if (data.score == null) return "Nessun modulo valutabile";
    if (data.score >= 85) return "Ottima";
    if (data.score >= 65) return "Buona";
    if (data.score >= 40) return "Da migliorare";
    return "Critica";
  })();

  return (
    <div data-testid="data-quality-page" className="space-y-4">
      <PageHeader
        eyebrow="Qualità dati"
        title="Data Quality Center"
        subtitle="Scopri quanto sono affidabili le analisi del gestionale"
        actions={
          <div className="flex items-end gap-2 flex-wrap">
            <div>
              <Label className="text-xs">Periodo</Label>
              <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                      data-testid="dq-period-select"
                      className="border rounded px-2 py-1 text-sm bg-white">
                <option value={7}>Ultimi 7 giorni</option>
                <option value={30}>Ultimi 30 giorni</option>
                <option value={90}>Ultimi 90 giorni</option>
              </select>
            </div>
            <Button size="sm" variant="outline" onClick={load} data-testid="dq-refresh-btn">
              <RefreshCw size={12} className="mr-1" /> Aggiorna
            </Button>
          </div>
        }
      />

      {loading || !data ? (
        <div className="text-sm text-neutral-500">Caricamento…</div>
      ) : (
        <>
          {/* HERO SCORE */}
          <div className="border border-stone-300 rounded-lg p-6 bg-white" data-testid="dq-hero">
            <div className="flex flex-wrap items-baseline justify-between gap-3">
              <div>
                <div className="label-eyebrow">Data Quality Score</div>
                <div className="text-5xl font-serif tabular-nums" data-testid="dq-score">
                  {data.score == null ? "—" : `${data.score}`}
                  {data.score != null && <span className="text-2xl text-neutral-400">/100</span>}
                </div>
                <div className="text-sm text-neutral-600 mt-1">{overallLabel}</div>
              </div>
              <div className="text-right">
                <div className="text-xs text-neutral-500">Rappresentatività</div>
                <div className="text-lg font-medium" data-testid="dq-rep">
                  {data.represented_weight}/{data.total_weight}
                  <span className="text-xs text-neutral-500 ml-1">({data.score_confidence_label})</span>
                </div>
                <div className="text-[11px] text-neutral-500 mt-1">
                  {data.included_areas.length}/9 aree valutabili
                </div>
              </div>
            </div>
            {data.note && (
              <div className="mt-3 rounded border border-blue-200 bg-blue-50 p-2 text-xs text-blue-900 flex items-start gap-2" data-testid="dq-note">
                <Info size={12} className="mt-0.5" />
                <span>{data.note}</span>
              </div>
            )}
            {data.observable_days && (
              <div className="mt-3 text-xs text-neutral-500" data-testid="dq-obs-days">
                <ShieldCheck size={12} className="inline mr-1" />
                Giornate osservabili nel periodo: <b>{data.observable_days.count}</b>
                <span className="ml-2 text-neutral-400">
                  (chiusure {data.observable_days.sources?.confirmed} · letture {data.observable_days.sources?.readings} · tracking {data.observable_days.sources?.tracking} · sales {data.observable_days.sources?.sales})
                </span>
              </div>
            )}
          </div>

          {/* DA SISTEMARE */}
          {data.actions?.length > 0 && (
            <div className="border rounded-lg p-4 bg-white" data-testid="dq-actions-panel">
              <div className="label-eyebrow mb-3">Da sistemare</div>
              <ul className="space-y-2">
                {data.actions.slice(0, 8).map((a, i) => (
                  <li key={i} className="flex justify-between items-start gap-2 border-b border-stone-100 pb-2 last:border-0">
                    <div>
                      <div className={`text-sm font-medium ${SEV_META[a.severity]?.color || ""}`}>
                        {SEV_META[a.severity]?.emoji} {a.title}
                      </div>
                      {a.detail && <div className="text-xs text-neutral-600 mt-0.5">{a.detail}</div>}
                    </div>
                    {a.cta_href && (
                      <Link to={a.cta_href}>
                        <Button size="sm" variant="outline">
                          {a.cta_label} <ArrowRight size={12} className="ml-1" />
                        </Button>
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* PANORAMICA 9 AREE */}
          <div className="space-y-2" data-testid="dq-areas-list">
            {data.areas.map((a) => (
              <AreaCard key={a.area} area={a} expanded={!!expanded[a.area]}
                onToggle={() => setExpanded((e) => ({ ...e, [a.area]: !e[a.area] }))} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
